import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ConfigurationDto {
  id?: number;
  key: string;
  value: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  isEncrypted: boolean;
  category?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  private http = inject(HttpClient);

  private apiUrl = `${environment.apiUrl}/v1.0/configurations`;
  private configCache = new Map<string, string>();
  private allConfigs: ConfigurationDto[] = [];
  private isInitialized = false;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  /** Forgets the settings, for when nobody is signed in: they belong to the signed-in company. */
  reset(): void {
    this.allConfigs = [];
    this.configCache.clear();
    this.isInitialized = false;
  }

  /**
   * Initialize configurations - to be called on app startup
   * Returns a Promise for use with APP_INITIALIZER
   */
  async initializeConfigurations(): Promise<void> {
    try {
      const configs = await firstValueFrom(this.http.get<ConfigurationDto[]>(this.apiUrl));
      this.allConfigs = configs;
      this.configCache.clear();

      // Populate cache
      configs.forEach((config) => {
        this.configCache.set(config.key, config.value);
      });

      this.isInitialized = true;
      console.log('Configurations loaded successfully:', configs.length);
    } catch (error) {
      console.error('Failed to load configurations:', error);
      this.isInitialized = false;
      // Don't throw - allow app to start even if config fails
    }
  }

  /**
   * Get configuration value by key from cache
   */
  getConfig(key: string): string | undefined {
    return this.configCache.get(key);
  }

  /**
   * Get configuration value with fallback
   */
  getConfigOrDefault(key: string, defaultValue: string): string {
    return this.configCache.get(key) ?? defaultValue;
  }

  /**
   * Get boolean configuration value
   */
  getBooleanConfig(key: string, defaultValue: boolean = false): boolean {
    const value = this.configCache.get(key);
    if (!value) return defaultValue;
    return value === 'true' || value === 'oui' || value === '1';
  }

  /**
   * Check if configurations are loaded
   */
  isConfigLoaded(): boolean {
    return this.isInitialized;
  }

  /**
   * Get all cached configurations
   */
  getCachedConfigurations(): ConfigurationDto[] {
    return [...this.allConfigs];
  }

  /**
   * Refresh configurations from server
   */
  async refreshConfigurations(): Promise<void> {
    await this.initializeConfigurations();
  }

  // API Methods (for admin/config page)

  getConfigurationByKey(key: string): Observable<ConfigurationDto> {
    return this.http.get<ConfigurationDto>(`${this.apiUrl}/${key}`);
  }

  getAllConfigurations(): Observable<ConfigurationDto[]> {
    return this.http.get<ConfigurationDto[]>(this.apiUrl).pipe(
      tap((configs) => {
        // Update cache when fetching all configurations
        this.allConfigs = configs;
        this.configCache.clear();
        configs.forEach((config) => {
          this.configCache.set(config.key, config.value);
        });
      }),
    );
  }

  postAllConfigurations(configurations: ConfigurationDto[]): Observable<ConfigurationDto[]> {
    return this.http.post<ConfigurationDto[]>(`${this.apiUrl}/all`, configurations).pipe(
      tap((configs) => {
        // Update cache after saving
        this.allConfigs = configs;
        this.configCache.clear();
        configs.forEach((config) => {
          this.configCache.set(config.key, config.value);
        });
      }),
    );
  }
}
